import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Spin } from 'antd'
import { useMutation, useLazyQuery, useQuery } from '@apollo/client'
import { MerchantLocation } from '@slerp/controls'
import { useSession } from 'packages/@slerp/accounts'
import { useMerchant } from 'hooks/useGlobalContext/useMerchant'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'
import { useIntegration } from 'hooks/useGlobalContext/useIntegration'
import CommonHeader from '../Common/CommonHeader'
import { ManageRoot } from '../styles'
import { LoadingRoot, FormWidth } from './styles'
import { REMOVE_INTEGRATION, CREATE_INTEGRATION } from '../../queries'
import { LEAT_HELP_URL, PartnerName } from '../../constants'
import InterstitialStep from './InterstitialStep'
import PatEntryStep from './PatEntryStep'
import TokenRejectedStep from './TokenRejectedStep'
import StoreLinkingStep from './StoreLinkingStep'
import ConnectStoresStep from './ConnectStoresStep'
import DisconnectModal from './DisconnectModal'
import {
  UPDATE_LEAT_PERSONAL_ACCESS_TOKEN,
  FETCH_LEAT_STORES,
  GET_LEAT_STORE_SETTINGS,
  GET_CUSTOMER_COUNT,
  UPDATE_STORE_INTEGRATION_SETTINGS,
  UPDATE_LOYALTY_IMPLEMENTATION,
  SYNC_LEAT_CATALOGUE
} from './queries'
import {
  ConnectStoresRow,
  LeatStore,
  LeatStoreLink,
  LoyaltyImplementation
} from './types'
import { getActiveStores, findStoreLink, isLeatConnected } from './utils'
import { useManageRootScrollport } from './useManageRootScrollport'

type LeatStep =
  | 'interstitial'
  | 'pat-entry'
  | 'token-rejected'
  | 'store-linking'
  | 'connect-stores'

const ManageLeat = () => {
  const navigate = useNavigate()
  const { addMessage } = useMessageStore()
  const { merchant } = useSession()
  const { merchantData, merchantLocations, refetchMerchantData } = useMerchant()
  const activeStores = getActiveStores(merchantLocations)
  // BE returns lowercase ('leat'); the enum is uppercase.
  const loyaltyImplementation = merchantData?.loyalty_implementation
    ? (merchantData.loyalty_implementation.toUpperCase() as LoyaltyImplementation)
    : null

  const [step, setStep] = useState<LeatStep>('interstitial')
  const [pendingToken, setPendingToken] = useState('')
  const [leatStores, setLeatStores] = useState<LeatStore[]>([])
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false)
  // Covers the per-store unlink loop too — those calls have no useMutation
  // `loading` flag of their own, so without this the modal spinner and the
  // page's other actions unblock mid-disconnect.
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [isSavingStores, setIsSavingStores] = useState(false)
  // Only "LINK STORES" from an already-populated store list has somewhere to
  // go back to — the fresh connect flow straight after PAT entry doesn't.
  const [cameFromLinkStore, setCameFromLinkStore] = useState(false)

  const { ref: manageRootRef, height: manageRootHeight } =
    useManageRootScrollport()

  // A shared cancelled-boolean can't tell "cancelled" from "a newer attempt in
  // flight", so a fast Cancel-then-retry would let attempt 1 clobber attempt 2.
  const attemptIdRef = useRef(0)

  // Snapshot taken when the step opens: a partial-failure retry's refetch
  // updates `links` mid-visit, which would otherwise make the retry look like
  // a repeat visit and skip the one-time catalogue sync.
  const isFirstConfirmRef = useRef(false)

  const { merchantIntegrations, setMerchantIntegrations } = useIntegration()

  const { data: storeLinksData, refetch: refetchStoreLinks } = useQuery(
    GET_LEAT_STORE_SETTINGS,
    {
      variables: { merchantId: merchant?.id },
      skip: !merchant?.id
    }
  )
  const links: LeatStoreLink[] = storeLinksData?.stores ?? []

  const { data: customerCountData } = useQuery(GET_CUSTOMER_COUNT, {
    variables: { merchantId: merchant?.id },
    skip: !merchant?.id
  })
  const customerCount: number | null =
    customerCountData?.customers_aggregate?.aggregate?.count ?? null

  // The auto-skip below makes a second async hop after mount — this keeps the
  // interstitial hidden until the step it lands on settles.
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false)

  const isAlreadyConnected = Boolean(
    merchantIntegrations?.leatPersonalAccessTokenSet
  )

  const [createIntegration, { loading: isCreating }] =
    useMutation(CREATE_INTEGRATION)

  const [updateMerchantIntegrationSettings, { loading: isUpdatingSettings }] =
    useMutation(UPDATE_LEAT_PERSONAL_ACCESS_TOKEN)

  const [fetchLeatStores, { loading: isFetchingStores }] = useLazyQuery(
    FETCH_LEAT_STORES,
    { fetchPolicy: 'network-only' }
  )

  const [removeIntegration] = useMutation(REMOVE_INTEGRATION)

  const [updateStoreIntegrationSettings] = useMutation(
    UPDATE_STORE_INTEGRATION_SETTINGS
  )
  const [updateLoyaltyImplementation] = useMutation(
    UPDATE_LOYALTY_IMPLEMENTATION
  )
  const [syncLeatCatalogue] = useMutation(SYNC_LEAT_CATALOGUE)

  const handleGetStarted = () => {
    setStep('pat-entry')
  }

  // An error and a resolved-but-unexpected data shape both count as failure,
  // toasted exactly once.
  const runMutation = async <TData,>(
    mutate: (options: {
      variables: Record<string, unknown>
      onError: () => void
    }) => Promise<{ errors?: unknown; data?: TData | null }>,
    variables: Record<string, unknown>,
    {
      isSuccess,
      errorMessage,
      attemptId
    }: {
      isSuccess: (data: TData | null | undefined) => boolean
      errorMessage: string
      attemptId?: number
    }
  ): Promise<boolean> => {
    // A stale response — Cancel, or a newer attempt — counts as neither.
    const isCurrent = () =>
      attemptId === undefined || attemptIdRef.current === attemptId

    const result = await mutate({
      variables,
      onError: () => {
        if (!isCurrent()) return
        addMessage({ type: 'error', content: errorMessage })
      }
    })
    if (!isCurrent()) return false
    if (result.errors) return false // already toasted via onError above
    if (!isSuccess(result.data)) {
      addMessage({ type: 'error', content: errorMessage })
      return false
    }
    return true
  }

  // Returns the list rather than leaving callers to read setLeatStores, so they
  // can route on it without racing the state update.
  const fetchAndSetStores = async (
    attemptId?: number
  ): Promise<LeatStore[] | false> => {
    const storesResult = await fetchLeatStores()
    if (attemptId !== undefined && attemptIdRef.current !== attemptId) {
      return false
    }
    if (storesResult.error) return false

    const stores = storesResult.data?.fetchLeatStores ?? []
    setLeatStores(stores)
    return stores
  }

  // Once per mount: otherwise Cancel on Token Rejected — where an invalid
  // saved token lands — would bounce back here, never to a real interstitial.
  const autoSkipAttemptedRef = useRef(false)

  useEffect(() => {
    if (autoSkipAttemptedRef.current) return
    if (step !== 'interstitial') return

    if (!merchantIntegrations?.leatPersonalAccessTokenSet) {
      autoSkipAttemptedRef.current = true
      return
    }

    autoSkipAttemptedRef.current = true
    setIsAutoAdvancing(true)
    fetchAndSetStores().then((storesFetched) => {
      setStep(storesFetched !== false ? 'store-linking' : 'token-rejected')
      setIsAutoAdvancing(false)
    })
  }, [merchantIntegrations, step])

  // Patches the Loader/Context value directly rather than round-tripping a
  // refetch — without this, connecting or disconnecting and then navigating
  // away and back in the same SPA session would read a stale value.
  const setLeatConnectionStatus = (connected: boolean) => {
    if (!merchantIntegrations) return
    setMerchantIntegrations({
      ...merchantIntegrations,
      leatPersonalAccessTokenSet: connected
    })
  }

  // Scoped to active stores — an archived store's stale leat_store_id
  // shouldn't count against "no active store has ever been linked".
  const computeIsFirstConfirm = () =>
    !activeStores.some((store) => isLeatConnected(store.id, links))

  // Saving the token doesn't validate it against Leat, so only fetchLeatStores'
  // failure routes to Token Rejected; a settings-save failure is infra.
  const saveTokenAndFetchStores = async (token: string, attemptId: number) => {
    const saved = await runMutation(
      updateMerchantIntegrationSettings,
      { token },
      {
        isSuccess: (data) =>
          Boolean(
            data?.updateMerchantIntegrationSettings?.leatPersonalAccessTokenSet
          ),
        errorMessage: 'Failed to save your Leat token. Please try again.',
        attemptId
      }
    )
    if (!saved) return
    setLeatConnectionStatus(true)

    const storesFetched = await fetchAndSetStores(attemptId)
    if (attemptIdRef.current !== attemptId) return
    if (storesFetched === false) {
      setStep('token-rejected')
      return
    }
    // A just-connected visit always lands on the connect form, however many
    // stores are already linked — unless there's no row to show, where
    // StoreLinkingStep's own empty branches are right.
    const canShowConnectForm =
      storesFetched.length > 0 && activeStores.length > 0
    if (canShowConnectForm) {
      isFirstConfirmRef.current = computeIsFirstConfirm()
    }
    setCameFromLinkStore(false)
    setStep(canShowConnectForm ? 'connect-stores' : 'store-linking')
  }

  const handleContinue = async (token: string) => {
    const attemptId = ++attemptIdRef.current
    setPendingToken(token)

    // A token already saved (URL revisited rather than disconnected first)
    // means the integration exists; re-creating it would be redundant.
    if (!isAlreadyConnected) {
      const created = await runMutation(
        createIntegration,
        { partnerName: PartnerName.LEAT },
        {
          isSuccess: (data) => Boolean(data?.createIntegration),
          errorMessage: 'Failed to start connecting Leat. Please try again.',
          attemptId
        }
      )
      if (!created) return
    }

    await saveTokenAndFetchStores(token, attemptId)
  }

  const handleTryAgain = async (token: string) => {
    const attemptId = ++attemptIdRef.current
    setPendingToken(token)
    await saveTokenAndFetchStores(token, attemptId)
  }

  const handleCancel = () => {
    // Bumping the id without starting an attempt invalidates the in-flight one.
    attemptIdRef.current += 1
    setStep('interstitial')
  }

  const handleRecheck = async () => {
    const storesFetched = await fetchAndSetStores()
    if (storesFetched === false) {
      setStep('token-rejected')
    }
  }

  const handleOpenConnectForm = () => {
    isFirstConfirmRef.current = computeIsFirstConfirm()
    setCameFromLinkStore(true)
    setStep('connect-stores')
  }

  const handleBackFromConnectForm = () => {
    setCameFromLinkStore(false)
    setStep('store-linking')
  }

  // updateStoreIntegrationSettings is per-store with no batch variant, so
  // unmodified rows would be pure redundant round trips.
  const getChangedRows = (rows: ConnectStoresRow[]): ConnectStoresRow[] =>
    rows.filter((row) => {
      const existing = findStoreLink(row.storeId, links)
      const prevLeatStoreId =
        existing?.store_integration_settings.leat_store_id ?? null
      const prevPush =
        existing?.store_integration_settings.leat_order_push_enabled ?? true
      return (
        row.leatStoreId !== prevLeatStoreId ||
        row.leatOrderPushEnabled !== prevPush
      )
    })

  // Rows are independent, so they go concurrently, each caught on its own so
  // one failure doesn't cancel the rest.
  const saveStoreRows = async (rows: ConnectStoresRow[]): Promise<boolean> => {
    const results = await Promise.all(
      getChangedRows(rows).map((row) =>
        updateStoreIntegrationSettings({
          variables: {
            partnerName: PartnerName.LEAT,
            storeId: row.storeId,
            leatStoreId: row.leatStoreId,
            leatOrderPushEnabled: row.leatOrderPushEnabled
          }
        }).catch(() => null)
      )
    )

    const hasFailure = results.some(
      (result) =>
        !result || result.errors || !result.data?.updateStoreIntegrationSettings
    )
    if (hasFailure) {
      addMessage({
        type: 'error',
        content: 'Failed to save one or more store links. Please try again.'
      })
      return false
    }
    return true
  }

  // No-op when the merchant is already on this implementation — both Confirm &
  // Connect and Save call it unconditionally.
  const saveLoyaltyIfChanged = async (isLeat: boolean): Promise<boolean> => {
    const nextImplementation = isLeat
      ? LoyaltyImplementation.LEAT
      : LoyaltyImplementation.SLERP
    if (nextImplementation === loyaltyImplementation) return true

    const switched = await runMutation(
      updateLoyaltyImplementation,
      { merchantId: merchant?.id, loyaltyImplementation: nextImplementation },
      {
        isSuccess: (data) =>
          data?.updateMerchant?.loyaltyImplementation === nextImplementation,
        errorMessage: 'Failed to switch your loyalty engine. Please try again.'
      }
    )
    // Awaited so a caller that advances the step next doesn't render against a
    // stale loyaltyImplementation.
    if (switched) await refetchMerchantData()
    return switched
  }

  // Only a true first confirm kicks off the loyalty switch and catalogue sync;
  // a repeat visit via "LINK STORES" only needs its changed rows saved.
  const handleConfirmConnectStores = async (rows: ConnectStoresRow[]) => {
    const isFirstConfirm = isFirstConfirmRef.current

    setIsSavingStores(true)
    const storesSaved = await saveStoreRows(rows)
    if (!storesSaved) {
      // Rows run concurrently, so rows that succeeded alongside the failing
      // one still need local `links` to catch up.
      await refetchStoreLinks()
      setIsSavingStores(false)
      return
    }

    // Independent — the refetch reads store-link data written by saveStoreRows,
    // not by this mutation — so they go concurrently.
    const [loyaltySaved] = await Promise.all([
      saveLoyaltyIfChanged(true),
      refetchStoreLinks()
    ])
    if (!loyaltySaved) {
      setIsSavingStores(false)
      return
    }

    if (isFirstConfirm) {
      const syncResult = await syncLeatCatalogue().catch(() => null)
      if (!syncResult || syncResult.errors) {
        addMessage({
          type: 'error',
          content:
            'Stores linked, but starting the catalogue sync failed. Please try again from your Leat settings.'
        })
        // Not fatal — the stores and loyalty already saved.
      }
    }

    setIsSavingStores(false)
    setStep('store-linking')
  }

  // Loyalty is already Leat by this point, so Save only touches row edits.
  const handleSaveStoreList = async (rows: ConnectStoresRow[]) => {
    setIsSavingStores(true)
    await saveStoreRows(rows)
    await refetchStoreLinks()
    setIsSavingStores(false)
  }

  const handleDisconnect = () => {
    setIsDisconnectModalOpen(true)
  }

  const handleCancelDisconnect = () => {
    setIsDisconnectModalOpen(false)
  }

  const unlinkStoreFromLeat = async (
    store: MerchantLocation
  ): Promise<string | null> => {
    try {
      const result = await updateStoreIntegrationSettings({
        variables: {
          partnerName: PartnerName.LEAT,
          storeId: store.id,
          leatStoreId: null,
          leatOrderPushEnabled: false
        }
      })
      if (result.errors) {
        return result.errors[0]?.message ?? 'Please try again.'
      }
      if (!result.data?.updateStoreIntegrationSettings) {
        return 'Please try again.'
      }
      return null
    } catch (error) {
      return error instanceof Error ? error.message : 'Please try again.'
    }
  }

  const handleConfirmDisconnect = async () => {
    setIsDisconnecting(true)
    // Every currently-linked store must be unlinked first — otherwise a
    // store is left pointing at a leat_store_id with no working PAT behind
    // it once the integration itself is removed.
    const connectedStores = activeStores.filter((store) =>
      isLeatConnected(store.id, links)
    )
    for (const store of connectedStores) {
      // eslint-disable-next-line no-await-in-loop
      const failureReason = await unlinkStoreFromLeat(store)
      if (failureReason) {
        addMessage({
          type: 'error',
          content: `Could not disconnect ${store.name}. ${failureReason}`
        })
        // isDisconnecting must land false before the modal closes: outside a
        // React event handler these setStates commit separately (React 17, no
        // auto-batching), freezing antd's exit transition on a loading button.
        setIsDisconnecting(false)
        setIsDisconnectModalOpen(false)
        return
      }
    }

    const disconnected = await runMutation(
      removeIntegration,
      { partnerName: PartnerName.LEAT },
      {
        isSuccess: (data) => Boolean(data?.removeIntegration),
        errorMessage: 'Failed to disconnect Leat. Please try again.'
      }
    )
    if (!disconnected) {
      setIsDisconnecting(false)
      setIsDisconnectModalOpen(false)
      return
    }

    // removeIntegration leaves the saved token in place. Clearing it is part of
    // disconnecting, not best-effort: a failure here keeps the merchant put.
    const nullified = await runMutation(
      updateMerchantIntegrationSettings,
      { token: null },
      {
        isSuccess: (data) =>
          data?.updateMerchantIntegrationSettings
            ?.leatPersonalAccessTokenSet === false,
        errorMessage: 'Could not disconnect. Please try again.'
      }
    )
    setIsDisconnecting(false)
    setIsDisconnectModalOpen(false)
    if (!nullified) return

    setLeatConnectionStatus(false)
    // Leat can no longer be the loyalty engine. Best-effort — disconnect
    // already succeeded, so a failure here still lets the merchant leave.
    await saveLoyaltyIfChanged(false)
    addMessage({
      type: 'message',
      content: 'Leat disconnected successfully!'
    })
    navigate('/integrations')
  }

  return (
    <ManageRoot
      ref={manageRootRef}
      style={
        manageRootHeight !== null
          ? { height: manageRootHeight, overflowY: 'auto' }
          : undefined
      }
    >
      <CommonHeader
        title='Connect Leat'
        onBack={() => navigate('/integrations')}
        helpLink={LEAT_HELP_URL}
      />

      {step === 'interstitial' &&
        (isAutoAdvancing ? (
          <LoadingRoot>
            <Spin />
          </LoadingRoot>
        ) : (
          <InterstitialStep onGetStarted={handleGetStarted} />
        ))}

      {step === 'pat-entry' && (
        <PatEntryStep
          initialToken={pendingToken}
          loading={isCreating || isUpdatingSettings || isFetchingStores}
          onContinue={handleContinue}
          onCancel={handleCancel}
        />
      )}

      {step === 'token-rejected' && (
        <TokenRejectedStep
          token={pendingToken}
          loading={isUpdatingSettings || isFetchingStores}
          onTryAgain={handleTryAgain}
          onCancel={handleCancel}
        />
      )}

      {step === 'connect-stores' && (
        <FormWidth>
          <ConnectStoresStep
            activeStores={activeStores}
            existingLinks={links}
            leatShops={leatStores}
            customerCount={customerCount}
            loyaltyImplementation={loyaltyImplementation}
            loading={isSavingStores}
            onConfirm={handleConfirmConnectStores}
            onBack={cameFromLinkStore ? handleBackFromConnectForm : undefined}
          />
        </FormWidth>
      )}

      {step === 'store-linking' && (
        <StoreLinkingStep
          activeStores={activeStores}
          leatStores={leatStores}
          links={links}
          loading={isSavingStores || isFetchingStores || isDisconnecting}
          onRecheck={handleRecheck}
          onDisconnect={handleDisconnect}
          onOpenConnectForm={handleOpenConnectForm}
          onSaveStoreList={handleSaveStoreList}
        />
      )}

      <DisconnectModal
        isOpen={isDisconnectModalOpen}
        loading={isDisconnecting}
        onOk={handleConfirmDisconnect}
        onCancel={handleCancelDisconnect}
      />
    </ManageRoot>
  )
}

export default ManageLeat
